import { ReactNode, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'
import PageErrorBoundary from './PageErrorBoundary'
import { useAuthStore } from '../stores/authStore'
import { getSidebarTheme } from '../config/sidebarThemes'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const userRole = useAuthStore((state) => state.user?.user_type)
  const { pathname } = useLocation()

  // Tint primary-* colours to the role's sidebar family. Set on <html>, not
  // on this tree, so dialogs portalled to <body> pick it up too; cleared on
  // the way out so the sign-in screen keeps the default palette.
  useEffect(() => {
    if (!userRole) return
    const root = document.documentElement
    root.dataset.roleTheme = getSidebarTheme(userRole).family
    return () => {
      delete root.dataset.roleTheme
    }
  }, [userRole])

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      <div className="flex h-full">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((collapsed) => !collapsed)} />
        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
          <Header />
          {/* `relative` keeps absolutely positioned descendants (sr-only text,
              popovers) inside this scroll column. Without it their containing
              block is the viewport, so they escape the overflow and give the
              window a second scrollbar with dead space below the page. */}
          <main className="relative flex-1 overflow-y-auto p-6">
            {/* A page that fails to draw shows a message here instead of blanking
                the app; a new page (new key) starts clean. */}
            <PageErrorBoundary key={pathname}>{children}</PageErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  )
}
